"use client";

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Languages } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { routing, type LandingLocale } from "@/i18n/routing";

export type LanguageSwitcherProps = Readonly<{
  ariaLabel: string;
  getLocaleLabel: (locale: LandingLocale) => string;
  locale: LandingLocale;
  placement?: "inline" | "floating" | "navigation";
  pathname?: string;
  onLocaleSelect?: (locale: LandingLocale) => void;
}>;

export function LanguageSwitcher({
  ariaLabel,
  getLocaleLabel,
  locale,
  placement = "inline",
  pathname,
  onLocaleSelect,
}: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuId = `language-switcher-menu-${placement}-${locale.toLowerCase()}`;

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const renderOptionContent = (option: LandingLocale) => {
    const isSelected = option === locale;
    return (
      <>
        <span className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-bold leading-none ${
              isSelected ? "bg-white/20 text-white" : "bg-primary/10 text-primary"
            }`}
          >
            {option}
          </span>
          <span>{getLocaleLabel(option)}</span>
        </span>
        {isSelected ? <Check className="h-4 w-4" aria-hidden={true} /> : null}
      </>
    );
  };

  const rootClassName = placement === "floating"
    ? "fixed right-3 top-[5.5rem] z-[90] inline-flex shrink-0 sm:right-4 sm:top-[5.75rem]"
    : placement === "navigation"
      ? "site-header__language"
      : "relative inline-flex shrink-0";
  const buttonClassName = placement === "navigation"
    ? "site-header__language-button"
    : "inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white/75 text-slate-600 shadow-sm backdrop-blur transition hover:border-slate-400 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60";

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={buttonClassName}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Languages className="h-5 w-5" aria-hidden={true} />
        <ChevronDown className="sr-only" aria-hidden={true} />
      </button>

      {isOpen ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[70] w-56 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-xl ring-1 ring-slate-200"
        >
          {routing.locales.map((option) => {
            const isSelected = option === locale;
            const className = `flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium transition ${
              isSelected
                ? "bg-slate-700 text-white"
                : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            }`;

            if (pathname) {
              return (
                <Link
                  key={option}
                  href={pathname}
                  locale={option}
                  role="menuitem"
                  aria-current={isSelected ? "true" : undefined}
                  className={className}
                  prefetch={false}
                  onClick={() => setIsOpen(false)}
                >
                  {renderOptionContent(option)}
                </Link>
              );
            }

            return (
              <button
                key={option}
                type="button"
                role="menuitem"
                aria-current={isSelected ? "true" : undefined}
                className={className}
                onClick={() => {
                  setIsOpen(false);
                  onLocaleSelect?.(option);
                }}
              >
                {renderOptionContent(option)}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
